//#region src/utils/brandingConfig.ts
/**
* Extracts branding configuration from DOM meta tags injected by Cloudflare Worker
* The result is cached since branding is injected server-side and never changes.
*/
function getBrandingConfigFromDOM() {
	if (typeof window === "undefined") return {};
	try {
		const config = {};
		const titleElement = document.querySelector("title");
		if (titleElement) config.title = titleElement.textContent || "";
		const descriptionMeta = document.querySelector("meta[name=\"description\"]");
		if (descriptionMeta) config.description = descriptionMeta.getAttribute("content") || "";
		const keywordsMeta = document.querySelector("meta[name=\"keywords\"]");
		if (keywordsMeta) config.keywords = keywordsMeta.getAttribute("content") || "";
		const phoneNumberMeta = document.querySelector("meta[name=\"phone-number\"]");
		if (phoneNumberMeta) config.phoneNumber = phoneNumberMeta.getAttribute("content") || "";
		const branchSelectionMethodMeta = document.querySelector("meta[name=\"branch-selection-method\"]");
		if (branchSelectionMethodMeta) {
			const value = branchSelectionMethodMeta.getAttribute("content") || "";
			if (value === "MAP" || value === "AREAS_DROPDOWN") config.branchSelectionMethod = value;
		}
		const brandIdMeta = document.querySelector("meta[name=\"brand\"]");
		if (brandIdMeta) config.brandId = brandIdMeta.getAttribute("content") || "";
		const logoMeta = document.querySelector("meta[name=\"logo\"]");
		if (logoMeta) config.logo = logoMeta.getAttribute("content") || "";
		const bannerMeta = document.querySelector("meta[name=\"banner\"]");
		if (bannerMeta) try {
			config.bannerImages = JSON.parse(bannerMeta.getAttribute("content") || "[]");
		} catch (e) {
			console.warn("Failed to parse banner images from meta tag:", e);
		}
		const socialMediaMeta = document.querySelector("meta[name=\"social-media\"]");
		if (socialMediaMeta) try {
			config.socialMedia = JSON.parse(socialMediaMeta.getAttribute("content") || "{}");
		} catch (e) {
			console.warn("Failed to parse social media from meta tag:", e);
		}
		const themeColorMeta = document.querySelector("meta[name=\"theme-color\"]");
		if (themeColorMeta) config.themeColor = themeColorMeta.getAttribute("content") || "";
		const ogImageMeta = document.querySelector("meta[property=\"og:image\"]");
		if (ogImageMeta) config.ogImage = ogImageMeta.getAttribute("content") || "";
		const twitterImageMeta = document.querySelector("meta[name=\"twitter:image\"]");
		if (twitterImageMeta) config.twitterImage = twitterImageMeta.getAttribute("content") || "";
		const faviconLink = document.querySelector("link[rel=\"icon\"]");
		if (faviconLink) config.favicon = faviconLink.getAttribute("href") || "";
		const brandColor = getComputedStyle(document.documentElement).getPropertyValue("--brand-color").trim();
		if (brandColor) config.colors = {
			brandColor: brandColor || "#000000",
			discountedPriceText: "#ff0000",
			actionButtonsBackground: brandColor || "#000000",
			actionButtonsText: "#ffffff",
			footerBackground: "#f8f9fa",
			footerText: "#333333"
		};
		return config;
	} catch (error) {
		console.error("Error extracting branding config from DOM:", error);
		return {};
	}
}
let cachedFullConfig = null;
/**
* Creates a complete BrandingConfig with fallback values
* The result is cached since branding is injected server-side and never changes.
*/
function createBrandingConfig(domConfig) {
	if (!domConfig && cachedFullConfig) return cachedFullConfig;
	const extractedConfig = domConfig || getBrandingConfigFromDOM();
	const config = {
		title: extractedConfig.title || "Restaurant",
		description: extractedConfig.description || "Welcome to our restaurant",
		keywords: extractedConfig.keywords || "restaurant, food, delivery",
		branchSelectionMethod: extractedConfig.branchSelectionMethod || "MAP",
		brandId: extractedConfig.brandId || "",
		logo: extractedConfig.logo || "",
		favicon: extractedConfig.favicon || "/favicon.ico",
		phoneNumber: extractedConfig.phoneNumber || "",
		themeColor: extractedConfig.themeColor || "#000000",
		backgroundColor: extractedConfig.backgroundColor || "#ffffff",
		colors: {
			brandColor: extractedConfig.colors?.brandColor || "#000000",
			discountedPriceText: extractedConfig.colors?.discountedPriceText || "#ff0000",
			actionButtonsBackground: extractedConfig.colors?.actionButtonsBackground || "#000000",
			actionButtonsText: extractedConfig.colors?.actionButtonsText || "#ffffff",
			footerBackground: extractedConfig.colors?.footerBackground || "#f8f9fa",
			footerText: extractedConfig.colors?.footerText || "#333333"
		},
		socialMedia: extractedConfig.socialMedia || {},
		bannerImages: extractedConfig.bannerImages || [],
		ogImage: extractedConfig.ogImage || extractedConfig.logo || "",
		twitterImage: extractedConfig.twitterImage || extractedConfig.ogImage || extractedConfig.logo || "",
		canonicalUrl: extractedConfig.canonicalUrl || window.location.origin,
		manifest: extractedConfig.manifest || "/manifest.json",
		preloadResources: extractedConfig.preloadResources || [],
		analytics: extractedConfig.analytics
	};
	if (!domConfig) cachedFullConfig = config;
	return config;
}
/**
* Hook to get branding configuration in React components
*/
function useBrandingConfig() {
	return createBrandingConfig();
}

//#endregion
export { createBrandingConfig, getBrandingConfigFromDOM, useBrandingConfig };
//# sourceMappingURL=brandingConfig.mjs.map